// src/modules/app-views/crm/pages/Boards/modals/CrmParametersModal/tabs/TemperaturesTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm, Modal, Form, Input, ColorPicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TemperaturesTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  const [temperatures, setTemperatures] = useState(() => {
    const moduleId = storedModule?.id || 'crm';
    const raw = appService.getModulePreferences({ moduleId, category: 'temperatures' });
    return Array.isArray(raw) ? raw : [];
  });

  const visibleTemperatures = useMemo(() => temperatures.filter(t => !t.removed), [temperatures]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (showModal) {
      if (editingRecord) {
        form.setFieldsValue({
          label: editingRecord.label || '',
          color: editingRecord.color || '#1890ff',
        });
      } else {
        form.resetFields();
      }
    }
  }, [showModal, editingRecord, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
    form.resetFields();
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const moduleId = storedModule?.id || 'crm';

      const labelValue = (values.label || '').trim();
      const colorValue = typeof values.color === 'string'
        ? values.color
        : values.color?.toHexString?.() || values.color?.hex || '#1890ff';

      // Check for duplicates
      const isDuplicate = temperatures.some(
        t =>
          t.uid !== editingRecord?.uid &&
          !t.removed &&
          (t.label || '').toLowerCase() === labelValue.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);

      let updated;
      if (editingRecord) {
        updated = temperatures.map(t =>
          t.uid === editingRecord.uid ? { ...t, label: labelValue, color: colorValue } : t
        );
      } else {
        updated = [
          ...temperatures,
          { uid: generateUidV4(), label: labelValue, color: colorValue },
        ];
      }

      await appService.updateModulePreferences({ moduleId, category: 'temperatures', data: updated });

      setTemperatures(updated);
      appService.message({ flag: 's', text: 'saved_successfully' });
      setShowModal(false);
      setEditingRecord(null);
      form.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // validation error
      console.error('[TemperaturesTab] Save error:', error);
      appService.message({ flag: 'e', text: 'error_saving' });
    } finally {
      setLoading(false);
    }
  }, [form, temperatures, editingRecord, setLoading, storedModule?.id]);

  const handleDelete = useCallback(
    async record => {
      const moduleId = storedModule?.id || 'crm';
      setLoading(true);
      try {
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'temperatures',
          uid: record.uid,
        });

        let updated;
        if (isInUse) {
          updated = temperatures.map(t =>
            t.uid === record.uid ? { ...t, removed: true } : t
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          updated = temperatures.filter(t => t.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        await appService.updateModulePreferences({ moduleId, category: 'temperatures', data: updated });
        setTemperatures(updated);
      } catch (error) {
        console.error('[TemperaturesTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [temperatures, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('label'),
      dataIndex: 'label',
      render: (text, record) => (
        <Flex align="center" gap={8}>
          {record.color && (
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                borderRadius: 3,
                backgroundColor: record.color,
                flexShrink: 0,
              }}
            />
          )}
          {translateX(text) || text}
        </Flex>
      ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleTemperatures}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_temperature')}
        </Button>
      </Flex>

      <Modal
        title={editingRecord ? translateX('edit') : translateX('add_temperature')}
        open={showModal}
        onCancel={handleCancel}
        destroyOnHidden
        centered
        mousePosition={mousePosition}
        getContainer={document.body}
        width={400}
        footer={
          <Flex justify="end" style={{ paddingTop: 8 }}>
            <Space>
              <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
                {translateX('cancel')}
              </Button>
              <Button type="primary" loading={loading} icon={<CheckOutlined />} onClick={handleSave}>
                {translateX('save')}
              </Button>
            </Space>
          </Flex>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="label"
            label={translateX('label')}
            rules={[{ required: true, message: () => translateX('required_field') }]}
          >
            <Input maxLength={50} style={{ height: 40 }} disabled={loading} />
          </Form.Item>
          <Form.Item name="color" label={translateX('color')}>
            <ColorPicker format="hex" disabled={loading} />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TemperaturesTab;
